/**
 * Oil & Gas — Plume & Leak Analyzer  (Step 6b of the 8-step intelligence loop)
 *
 * Reads OGI survey missions that have completed AI analysis and surfaces
 * methane column-density data, plume bounding, and CH₄ flow-rate quantification
 * from the mission metadata. Provides a frame-by-frame telemetry timeline view.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Wind, MapPin, Activity, FlaskConical,
  RefreshCw, ChevronDown, ChevronUp, AlertTriangle,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import api from '../../services/api';
import { useOrgSettings } from '../../hooks/useOrgSettings';

// ─── Types ────────────────────────────────────────────────────────────────────

interface OgiMission {
  id: string;
  name: string;
  status: string;
  created_at: string;
  metadata?: {
    vertical?: string;
    ai_analysis_status?: string;
    corridor_name?: string;
    plume_rate_kg_hr?: number;
    plume_source_lat?: number;
    plume_source_lon?: number;
    plume_dispersion_m?: number;
    ch4_column_density_ppm?: number;
    ch4_peak_ppm?: number;
    thermal_anomaly_c?: number;
    diagnosis?: string;
    severity?: string;
    payload?: string;
    altitude_m?: number;
    [key: string]: any;
  };
}

// ─── Severity colour ──────────────────────────────────────────────────────────

function severityClass(sev?: string) {
  if (!sev) return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  const map: Record<string, string> = {
    critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    high:     'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    medium:   'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    low:      'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  };
  return map[sev] ?? map.low;
}

// ─── Plume rate bar ───────────────────────────────────────────────────────────

function PlumeRateBar({ rate, unitLabel }: { rate: number; unitLabel: string }) {
  const pct = Math.min(100, (rate / 200) * 100);
  const colour =
    rate >= 100 ? 'bg-red-500' :
    rate >= 40  ? 'bg-orange-500' :
    rate >= 10  ? 'bg-amber-400' :
    'bg-green-500';
  return (
    <div className="flex items-center gap-2 mt-1">
      <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
        <div className={`h-full rounded-full ${colour}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-mono text-gray-600 dark:text-gray-400 w-24 text-right">{rate} {unitLabel}</span>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export const PlumeLeakAnalyzerPage: React.FC = () => {
  const { uom } = useOrgSettings();
  const [missions, setMissions]     = useState<OgiMission[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterSev, setFilterSev]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/missions', { params: { mission_type: 'ogi_survey', limit: 100 } });
      const all: OgiMission[] = res.data?.missions ?? res.data?.data?.missions ?? [];
      setMissions(
        all.filter(
          (m) =>
            m.metadata?.vertical === 'oil_gas' &&
            m.metadata?.ai_analysis_status === 'complete' &&
            m.metadata?.plume_rate_kg_hr != null,
        ),
      );
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load missions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filterSev
    ? missions.filter((m) => m.metadata?.severity === filterSev)
    : missions;

  // Aggregate stats
  const totalPlume  = missions.reduce((s, m) => s + (m.metadata?.plume_rate_kg_hr ?? 0), 0);
  const maxPlume    = missions.length ? Math.max(...missions.map((m) => m.metadata?.plume_rate_kg_hr ?? 0)) : 0;
  const critCount   = missions.filter((m) => m.metadata?.severity === 'critical').length;
  const avgCh4      = missions.length
    ? missions.reduce((s, m) => s + (m.metadata?.ch4_column_density_ppm ?? 0), 0) / missions.length
    : 0;

  const SELECT_CLS = 'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Wind className="w-7 h-7 text-blue-500" /> Plume &amp; Leak Analyzer
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            OGI telemetry analysis — methane column-density, plume bounding, and CH₄ flow-rate quantification
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filterSev} onChange={(e) => setFilterSev(e.target.value)} className={SELECT_CLS}>
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* ── Stats ── */}
      {missions.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Surveys with Plume Data', value: missions.length,          colour: 'text-gray-900 dark:text-white' },
            { label: 'Critical Events',         value: critCount,                colour: 'text-red-700 dark:text-red-400' },
            { label: `Peak Rate (${uom.mass_rate_unit_label})`, value: maxPlume.toFixed(1), colour: 'text-orange-700 dark:text-orange-400' },
            { label: 'Avg CH₄ Density (ppm)',    value: avgCh4.toFixed(1),        colour: 'text-blue-700 dark:text-blue-400' },
          ].map(({ label, value, colour }) => (
            <Card key={label} padding="sm">
              <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
              <p className={`text-2xl font-bold mt-1 ${colour}`}>{value}</p>
            </Card>
          ))}
        </div>
      )}

      {/* ── Cumulative plume rate summary ── */}
      {missions.length > 0 && (
        <Card padding="sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
              <FlaskConical className="w-4 h-4 text-blue-500" /> Cumulative Detected Plume Rate
            </p>
            <span className="text-sm font-mono text-orange-600 dark:text-orange-400 font-bold">
              {totalPlume.toFixed(1)} {uom.mass_rate_unit_label} total
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Aggregated CH₄ flow-rate across all OGI surveys with completed analysis. Each figure is derived using
            the Stanford mass-balance method applied to OGI radiometric frames.
          </p>
        </Card>
      )}

      {/* ── Survey list ── */}
      <Card padding="none">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {filtered.length} OGI survey{filtered.length !== 1 ? 's' : ''} with plume analysis
          </p>
          {filterSev && (
            <button onClick={() => setFilterSev('')} className="text-xs text-gray-400 hover:text-gray-600 underline">
              Clear filter
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-14"><Spinner size="lg" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-14">
            <Wind className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">
              {missions.length === 0 ? 'No OGI surveys with plume data yet' : 'No surveys match the current filter'}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {missions.length === 0
                ? 'Complete an OGI survey in Flight Data & Analysis to populate plume metrics'
                : 'Try a different severity filter'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.map((m) => {
              const isExpanded = expandedId === m.id;
              const rate = m.metadata?.plume_rate_kg_hr ?? 0;
              return (
                <div key={m.id}>
                  <button
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 text-left"
                    onClick={() => setExpandedId(isExpanded ? null : m.id)}
                  >
                    <Wind className="w-4 h-4 flex-shrink-0 text-blue-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{m.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {m.metadata?.corridor_name ?? '—'} · {new Date(m.created_at).toLocaleDateString('en-GB')}
                      </p>
                      <PlumeRateBar rate={rate} unitLabel={uom.mass_rate_unit_label} />
                    </div>
                    {m.metadata?.severity && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize flex-shrink-0 ${severityClass(m.metadata.severity)}`}>
                        {m.metadata.severity}
                      </span>
                    )}
                    {isExpanded
                      ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  </button>

                  {isExpanded && (
                    <div className="px-5 pb-5 pt-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 space-y-4">

                      {/* Metrics grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                          { label: 'CH₄ Flow Rate',       value: `${rate} ${uom.mass_rate_unit_label}`,               icon: FlaskConical },
                          { label: 'Column Density',       value: m.metadata?.ch4_column_density_ppm != null ? `${m.metadata.ch4_column_density_ppm} ppm` : '—', icon: Activity },
                          { label: 'Peak Concentration',  value: m.metadata?.ch4_peak_ppm != null ? `${m.metadata.ch4_peak_ppm} ppm` : '—', icon: AlertTriangle },
                          { label: 'Thermal Anomaly',     value: m.metadata?.thermal_anomaly_c != null ? `+${m.metadata.thermal_anomaly_c}°C` : '—', icon: Wind },
                        ].map(({ label, value, icon: Icon }) => (
                          <div key={label} className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3">
                            <div className="flex items-center gap-1.5 mb-1">
                              <Icon className="w-3.5 h-3.5 text-blue-500" />
                              <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                            </div>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">{value}</p>
                          </div>
                        ))}
                      </div>

                      {/* Plume source location */}
                      {(m.metadata?.plume_source_lat != null || m.metadata?.plume_dispersion_m != null) && (
                        <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3">
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" /> Plume Source &amp; Dispersion
                          </p>
                          <div className="flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
                            {m.metadata?.plume_source_lat != null && (
                              <span>
                                Source: <strong className="text-gray-800 dark:text-gray-200 font-mono">
                                  {m.metadata.plume_source_lat.toFixed(5)}, {m.metadata.plume_source_lon?.toFixed(5)}
                                </strong>
                              </span>
                            )}
                            {m.metadata?.plume_dispersion_m != null && (
                              <span>Dispersion radius: <strong className="text-gray-800 dark:text-gray-200">{m.metadata.plume_dispersion_m} m</strong></span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* AI diagnosis */}
                      {m.metadata?.diagnosis && (
                        <div className="rounded-lg bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 p-3">
                          <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-1">AI Diagnosis</p>
                          <p className="text-sm text-gray-700 dark:text-gray-300">{m.metadata.diagnosis}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default PlumeLeakAnalyzerPage;

// Made with Bob
